/**
 * Chrome around the RFQ Create Wizard: step indicator, auto-save pill and
 * the "resume draft?" prompt. Presentational only.
 */
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { STEPS } from './rfqWizardTypes';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Check, Cloud, CloudOff, Loader2, PencilLine, X } from 'lucide-react';

export function WizardStepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center justify-between mb-8">
      {STEPS.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= step.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              {currentStep > step.id ? <Check className="h-5 w-5" /> : step.id}
            </div>
            <span className="text-xs mt-1 text-center max-w-[80px]">{step.title}</span>
          </div>
          {index < STEPS.length - 1 && (
            <div className={`h-0.5 w-12 mx-2 ${currentStep > step.id ? 'bg-primary' : 'bg-muted'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

interface WizardSaveStatusProps {
  /** False while the resume prompt is open or the RFQ is being created. */
  visible: boolean;
  status: AutoSaveStatus;
  /** Last successful save this session, or when a resumed draft was saved. */
  lastSavedAt: Date | null;
  /** Edits newer than the last save, still waiting for the debounce. */
  hasUnsavedChanges: boolean;
}

// CR-5461: small status pill showing draft save state.
//
// CR-5468 rework: this pill and the step-6 button both said "draft" but
// mean different things — this one auto-saves the WIZARD's progress so a
// refresh doesn't lose it, while "Save as Draft" creates a real RFQ record
// in draft status. The wording says progress, and only the button says RFQ.
//
// CR-6207: says "Unsaved changes" during the seconds before a save, so it
// never claims "saved" while newer edits are pending, and nothing at all
// until the user has changed something.
export function WizardSaveStatus({ visible, status, lastSavedAt, hasUnsavedChanges }: WizardSaveStatusProps) {
  if (!visible) return null;
  if (status === 'saving') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
        <Loader2 className="h-3 w-3 animate-spin" /> Saving your progress…
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-amber-600" title="Last auto-save failed; we will keep retrying." aria-live="polite">
        <CloudOff className="h-3 w-3" /> Progress not saved
      </span>
    );
  }
  if (hasUnsavedChanges) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground" title="Your latest changes will be saved in a few seconds." aria-live="polite">
        <PencilLine className="h-3 w-3" /> Unsaved changes
      </span>
    );
  }
  if (lastSavedAt) {
    return (
      <span
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
        title={`Your progress through this wizard is saved (${lastSavedAt.toLocaleString()}). The RFQ itself is not created until you finish.`}
        aria-live="polite"
      >
        <Cloud className="h-3 w-3" /> Progress saved — RFQ not created yet
      </span>
    );
  }
  return null;
}

interface WizardResumePromptProps {
  updatedAt: string;
  onClose: () => void;
  onResume: () => void;
  onDiscard: () => void;
}

// CR-5461: blocking resume prompt when an existing draft was found.
// Shown above the wizard so the user picks Resume or Start Fresh
// before auto-save engages.
export function WizardResumePrompt({ updatedAt, onClose, onResume, onDiscard }: WizardResumePromptProps) {
  const when = new Date(updatedAt).toLocaleString();
  return (
    <div className="bg-background min-h-[calc(100vh-200px)]">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Create New RFQ</h1>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Resume where you left off?</CardTitle>
            <CardDescription>
              You have unfinished progress in this wizard from {when}. It has not been
              created as an RFQ yet — pick up where you left off, or discard it and start again.
              Only your most recent progress is kept.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex gap-3">
            <Button onClick={onResume}>Resume draft</Button>
            <Button variant="outline" onClick={onDiscard}>Discard &amp; start fresh</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
